import type { ApproveWorkPeriodNode } from './types.graphql'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO, parseISO } from 'date-fns'
import { Checkmark } from '@/components_deprecated/Table/cells'
import { usePermissions, useUser } from '@/utils/hooks'
import { ApproveCommentModal } from './ApproveCommentModal'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import styled, { useTheme } from 'styled-components'
import { WorkPeriodDetail } from './WorkPeriodDetail'
import { combineDatesToReadableString } from '@/utils/date/combineDatesToReadableString'
import { getWeek } from '@/utils/date-fns/getWeek'
import { DietButton } from './components/DietButton'
import { Card } from '@/components_deprecated/Card'
import {
  useChangeDietPermission,
  useWorkPeriodApprovePermissions,
} from './hooks'
import { H2, H3, H4 } from '@/components'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import { useWorkPeriodMigrations } from './hooks/useWorkPeriodMigrations'
import { SalaryDayButton } from './components/SalaryDayButton'
import { AddonButton } from '../activities/components/overview/AddonCell'
import { SalarySettingSelect } from '@/components_deprecated/Select/SalarySettingSelect'
import SalaryGantt from '@/components/kibo-ui/gantt/salary-gantt'
import { PERMISSIONS } from '@/utils/permissions'
import { TOGGLE_ACTIVITY_EXCLUDE_FROM_SALARY_CALCULATION_MUTATION } from '../activities/mutations'
import { useMutation } from '@apollo/client'
import type { GanttFeature } from '@/components/kibo-ui/gantt'
import type { ExcludeActivityPromptData } from './ExcludeActivityPrompt'
import { ExcludeActivityPrompt } from './ExcludeActivityPrompt'

const CommentText = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.3;
`
const GroupWrapper = styled.div`
  margin: 2rem 0;
`

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;

  > h3 {
    margin: 1rem 0 0.25rem 0;
  }
  > h4 {
    margin: 1rem 0 0.5rem 0;
  }
`

const DietWrapper = styled.div`
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
`

interface ApprovalWrapperProps {
  $approved: boolean | null
}

const ApprovalWrapper = styled.div<ApprovalWrapperProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  padding: 1rem;

  border-radius: 8px;
  background-color: ${props =>
    props.$approved
      ? props.theme.colors.green100
      : props.$approved === null
      ? 'transparent'
      : props.theme.colors.red100};
  border: 1px solid ${props => props.theme.colors.gray8};

  &:first-child {
    border-right: 1px solid ${props => props.theme.colors.gray8};
  }
`

const ApprovalHalf = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  max-width: 100%;
`

const PlannedWork = styled.span`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  overflow-wrap: anywhere;
`

const PlannedWorkAndIcon = styled.span`
  display: flex;
  align-items: center;
  margin: 1rem 0;
`

const AddonsAsTextWrapper = styled.div`
  position: relative;
  margin: 1rem 0;
  border-top: 1px solid ${props => props.theme.colors.gray8};
  padding-top: 1rem;
  min-height: 40px;
`

export const WorkPeriodApproveCards: React.FC<{
  workPeriods: ApproveWorkPeriodNode[]
  loading: boolean
  initRefetch: () => void
}> = ({ workPeriods, initRefetch }) => {
  const translations = useTranslate({
    user: 'common.user',
    date: 'common.date',
    addons: 'salary.addons',
    approvedByUser: 'common.approved-by-driver',
    approvedByManager: 'common.approved-by-manager',
    comment: 'common.comment',
    managerComment: 'common.manager-comment',
    totalWork: 'common.total-work',
    weekN: ['common.week-n', { n: '0' }],
    diet: 'common.diet',
    transferedOvertimeToFlextime: 'salary.transfered-overtime-to-flextime',
  })

  const salaryConfiguration = useSalaryModuleConfiguration()
  const canEditDiet = useChangeDietPermission()

  const { hasPermissions } = usePermissions()

  const me = useUser()
  const theme = useTheme()
  const addPrompt = usePrompt()

  const { workPeriodApprovalState } = useWorkPeriodApprovePermissions()

  const { patchWorkPeriod, transferOvertimeToFlex } = useWorkPeriodMigrations()

  const [toggleActivityPaid] = useMutation(
    TOGGLE_ACTIVITY_EXCLUDE_FROM_SALARY_CALCULATION_MUTATION
  )

  const canMarkActivitiesAsNotPaid = hasPermissions(
    PERMISSIONS.activities.change.driveractivitypaidstatus
  )

  async function handleApproveWorkPeriodDriver(
    workPeriod: ApproveWorkPeriodNode
  ) {
    const { data } = await addPrompt<{
      comment: string
      approved: boolean
    } | null>(resolve => (
      <ApproveCommentModal
        existingComment={workPeriod.userComment}
        existingApproved={workPeriod.approvedByUser}
        onSubmit={resolve}
      />
    ))
    if (!data) return
    void patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          approvedByUser: data.approved,
          userComment: data.comment,
        },
      },
    })
    initRefetch()
  }

  async function handleApproveWorkPeriodManager(
    workPeriod: ApproveWorkPeriodNode
  ) {
    const { data } = await addPrompt<{
      comment: string
      approved: boolean
    } | null>(resolve => (
      <ApproveCommentModal
        existingComment={workPeriod.managerComment}
        existingApproved={workPeriod.approvedByManager}
        onSubmit={resolve}
      />
    ))
    if (!data) return

    void patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          approvedByManager: data.approved,
          managerComment: data.comment,
        },
      },
    })
    initRefetch()
  }

  async function showWorkPeriodDetails(workPeriod: ApproveWorkPeriodNode) {
    await addPrompt<{ id: string } | null>(resolve => (
      <WorkPeriodDetail workPeriodId={workPeriod.id} onSubmit={resolve} />
    ))
  }

  // Group work periods by week
  const workPeriodGroups = workPeriods.reduce(
    (acc, workPeriod) => {
      const weekStart = getWeek(parseISO(workPeriod.datetimeStart))

      if (!acc[weekStart]) {
        acc[weekStart] = []
      }
      acc[weekStart].push(workPeriod)

      return acc
    },
    {} as Record<string, ApproveWorkPeriodNode[]>
  )

  return (
    <span>
      {Object.entries(workPeriodGroups).map(group => {
        const weekNumber = group[0]
        const workPeriodGroup = group[1]

        return (
          <GroupWrapper key={weekNumber + workPeriodGroup[0]?.id}>
            <H2>{translations.weekN({ n: weekNumber })}</H2>
            {workPeriodGroup.map(workPeriod => {
              const {
                driverApproveDirty,
                managerApproveDirty,
                canApproveAsDriver,
                canApproveAsManager,
              } = workPeriodApprovalState(me.id, workPeriod)

              async function handleClickGanttFeature(feature: GanttFeature) {
                if (workPeriod.approvedByManager || !canMarkActivitiesAsNotPaid)
                  return
                const activity = workPeriod.activities.find(
                  a => a.id === feature.id
                )
                if (!activity) return

                const { data } =
                  await addPrompt<ExcludeActivityPromptData | null>(resolve => (
                    <ExcludeActivityPrompt
                      activity={activity}
                      onSubmit={resolve}
                    />
                  ))

                if (!data) return

                await toggleActivityPaid({
                  variables: {
                    activity: activity.id,
                    value: data.excludeFromSalaryCalculation,
                    excludeAfter: data.excludeAfter
                      ? formatISO(data.excludeAfter)
                      : null,
                    excludeBefore: data.excludeBefore
                      ? formatISO(data.excludeBefore)
                      : null,
                  },
                })
                initRefetch()
              }

              return (
                <StyledCard key={workPeriod.id}>
                  <div className="relative h-auto min-h-[200px]">
                    <H3>{workPeriod.user.fullName}</H3>
                    <PlannedWorkAndIcon>
                      <PlannedWork>
                        <b>
                          {workPeriod.calendarItem &&
                            combineDatesToReadableString(
                              parseISO(workPeriod.calendarItem.start),
                              parseISO(workPeriod.calendarItem.end),
                              false
                            )}
                        </b>
                        <span>
                          {combineDatesToReadableString(
                            parseISO(workPeriod.datetimeStart),
                            parseISO(workPeriod.datetimeEnd),
                            false
                          )}
                        </span>
                      </PlannedWork>

                      <Icon
                        icon="circle-info"
                        type="solid"
                        size="28"
                        cursor="pointer"
                        margin="0 0 0 0.5rem"
                        color={theme.colors.secondary}
                        onClick={() => showWorkPeriodDetails(workPeriod)}
                      />
                    </PlannedWorkAndIcon>

                    <DietWrapper>
                      <SalarySettingSelect
                        value={workPeriod.salarySetting?.id || null}
                        onChange={async value => {
                          await patchWorkPeriod({
                            variables: {
                              id: workPeriod.id,
                              input: {
                                salarySetting: value === '' ? null : value,
                              },
                            },
                          })
                          initRefetch()
                        }}
                      />
                      {workPeriod.salarySetting?.salaryType == 'DAY' && (
                        <SalaryDayButton
                          workPeriod={workPeriod}
                          salaryDay={workPeriod.salaryDay}
                          userId={workPeriod.user.id}
                          canEdit={
                            canApproveAsManager ||
                            (!workPeriod.approvedByManager &&
                              !workPeriod.approvedByUser)
                          }
                          border={true}
                          onChange={() => initRefetch()}
                          restrictWidth
                        />
                      )}
                      <DietButton
                        workPeriod={workPeriod}
                        userId={workPeriod.user.id}
                        canEdit={
                          canEditDiet &&
                          (canApproveAsManager ||
                            (!workPeriod.approvedByManager &&
                              !workPeriod.approvedByUser))
                        }
                        border={true}
                        onChange={() => initRefetch()}
                        restrictWidth
                      />
                      {/* Margin left right 1rem */}
                      {salaryConfiguration.canTransferWorkToAbsence && (
                        <div className="flex w-full items-center justify-between mt-2 px-4">
                          <div className="min-w-[105px]">
                            {translations.transferedOvertimeToFlextime}
                          </div>
                          <Icon
                            icon={
                              workPeriod.absenceHourTransaction
                                ? 'check'
                                : 'times'
                            }
                            color={
                              workPeriod.absenceHourTransaction
                                ? theme.colors.green
                                : undefined
                            }
                            onClick={() => {
                              if (workPeriod.absenceHourTransaction) return
                              void transferOvertimeToFlex({
                                variables: {
                                  workPeriod: workPeriod.id,
                                },
                              })
                            }}
                          />
                        </div>
                      )}
                    </DietWrapper>
                  </div>
                  <hr className="my-4 border-gray-200 dark:border-gray-700" />
                  {salaryConfiguration.driverApprove && (
                    <>
                      <h4>{translations.approvedByUser}</h4>
                      <ApprovalWrapper
                        $approved={
                          workPeriod.approvedByUser
                            ? true
                            : !driverApproveDirty
                            ? null
                            : false
                        }
                      >
                        <ApprovalHalf
                          onClick={() =>
                            canApproveAsDriver &&
                            handleApproveWorkPeriodDriver(workPeriod)
                          }
                        >
                          <Checkmark
                            checked={workPeriod.approvedByUser}
                            icon={
                              !driverApproveDirty ? 'circle-plus' : undefined
                            }
                            color={
                              !driverApproveDirty
                                ? theme.colors.gray3
                                : undefined
                            }
                          />
                          {workPeriod.userComment && (
                            <CommentText>{workPeriod.userComment}</CommentText>
                          )}
                        </ApprovalHalf>
                      </ApprovalWrapper>
                    </>
                  )}

                  {salaryConfiguration.managerApprove && (
                    <>
                      <h4>{translations.approvedByManager}</h4>
                      <ApprovalWrapper
                        $approved={
                          workPeriod.approvedByManager
                            ? true
                            : !managerApproveDirty
                            ? null
                            : false
                        }
                      >
                        <ApprovalHalf
                          onClick={() => {
                            if (!canApproveAsManager) return
                            void handleApproveWorkPeriodManager(workPeriod)
                          }}
                        >
                          <Checkmark
                            checked={workPeriod.approvedByManager}
                            icon={
                              !managerApproveDirty ? 'circle-plus' : undefined
                            }
                            color={
                              !managerApproveDirty
                                ? theme.colors.gray3
                                : undefined
                            }
                          />
                          {workPeriod.managerComment && (
                            <CommentText>
                              {workPeriod.managerComment}
                            </CommentText>
                          )}
                        </ApprovalHalf>
                      </ApprovalWrapper>
                    </>
                  )}
                  <AddonsAsTextWrapper>
                    <H4>{translations.addons}</H4>
                    <div className="absolute top-4 right-4">
                      <AddonButton
                        userId={workPeriod.user.id}
                        datetimeStart={parseISO(workPeriod.datetimeStart)}
                        datetimeEnd={parseISO(workPeriod.datetimeEnd)}
                        addonsAsText={[]}
                        restrictWidth={false}
                        onClick={() => initRefetch()}
                      />
                    </div>
                    {workPeriod.addonsAsText.map((text, index) => (
                      <CommentText key={index}>{text}</CommentText>
                    ))}
                  </AddonsAsTextWrapper>
                  {workPeriod.salarySetting && (
                    <SalaryGantt
                      workPeriodId={workPeriod.id}
                      activitiesOnly={
                        workPeriod.salarySetting.salaryType !== 'HOUR'
                      }
                      onClickFeature={feature =>
                        void handleClickGanttFeature(feature)
                      }
                    />
                  )}
                </StyledCard>
              )
            })}
          </GroupWrapper>
        )
      })}
    </span>
  )
}
